import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation } from "react-router-dom";

export interface PageBreadcrumbOverride {
  href?: string;
  label?: string;
  loading?: boolean;
}

type PageBreadcrumbOverrides = Record<string, PageBreadcrumbOverride>;

interface PageBreadcrumbContextValue {
  overrides: PageBreadcrumbOverrides;
  setOverrides: React.Dispatch<React.SetStateAction<PageBreadcrumbOverrides>>;
}

const PageBreadcrumbContext = createContext<PageBreadcrumbContextValue | null>(
  null
);

export const PageBreadcrumbProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { pathname } = useLocation();
  const [overrides, setOverrides] = useState<PageBreadcrumbOverrides>({});

  useEffect(() => {
    setOverrides({});
  }, [pathname]);

  const value = useMemo(
    () => ({ overrides, setOverrides }),
    [overrides, setOverrides]
  );

  return (
    <PageBreadcrumbContext.Provider value={value}>
      {children}
    </PageBreadcrumbContext.Provider>
  );
};

export const usePageBreadcrumbs = (overrides: PageBreadcrumbOverrides) => {
  const context = useContext(PageBreadcrumbContext);
  const serializedOverrides = JSON.stringify(overrides);
  const stableOverrides = useMemo(() => overrides, [serializedOverrides]);
  const setOverrides = context?.setOverrides;

  useEffect(() => {
    if (!setOverrides) return;

    setOverrides(stableOverrides);
  }, [setOverrides, stableOverrides]);
};

export const useResolvedPageBreadcrumbOverrides = () => {
  const context = useContext(PageBreadcrumbContext);

  if (!context) {
    return {};
  }

  return context.overrides;
};
